// BudgetProcurementPanel.js
import React, { useState } from "react";
import { DollarSign, ShoppingCart, Check, X, FileText } from "lucide-react";
import { toast } from "react-toastify";

const BudgetProcurementPanel = ({ project, onUpdate }) => {
    const [budgetStatus, setBudgetStatus] = useState(project.budgetStatus || "Pending");
    const [procurementStatus, setProcurementStatus] = useState(project.procurementStatus || "Pending");
    const [budgetNotes, setBudgetNotes] = useState(project.budgetNotes || "");
    const [procurementNotes, setProcurementNotes] = useState(project.procurementNotes || "");
    const [isEditing, setIsEditing] = useState(false);

    const handleSave = () => {
        // In a real app, this would save to the backend
        toast.success("Budget and procurement status updated!");
        setIsEditing(false);
        if (onUpdate) onUpdate();
    };

    const budgetItems = [
        { category: "Personnel", allocated: 45000, spent: 32000 },
        { category: "Equipment", allocated: 25000, spent: 18000 },
        { category: "Supplies", allocated: 15000, spent: 12000 },
        { category: "Transport", allocated: 10000, spent: 8500 },
        { category: "Training", allocated: 8000, spent: 5000 },
        { category: "Monitoring", allocated: 7000, spent: 4500 },
    ];

    const procurementItems = [
        { item: "Vehicles", status: "Delivered", date: "2023-05-15" },
        { item: "Computers", status: "Delivered", date: "2023-06-20" },
        { item: "Office Furniture", status: "Ordered", date: "2023-07-10" },
        { item: "Field Equipment", status: "Pending", date: "" },
    ];

    const totalBudget = budgetItems.reduce((sum, item) => sum + item.allocated, 0);
    const totalSpent = budgetItems.reduce((sum, item) => sum + item.spent, 0);
    const remainingBudget = totalBudget - totalSpent;
    const budgetUtilization = (totalSpent / totalBudget) * 100;

    return (
        <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Budget & Procurement Review</h3>
                <p className="text-sm text-blue-700">
                    Review project budget allocation and procurement plans for compliance with organizational policies and donor requirements.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Budget Overview */}
                <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-gray-800 flex items-center">
                            <DollarSign className="mr-2 text-green-600" size={20} />
                            Budget Overview
                        </h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                            budgetStatus === 'Approved' ? 'bg-green-100 text-green-800' : 
                            budgetStatus === 'Rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                            {budgetStatus}
                        </span>
                    </div>
                    
                    <div className="space-y-4">
                        <div>
                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                                <span>Budget Utilization</span>
                                <span>{budgetUtilization.toFixed(1)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div 
                                    className={`h-2.5 rounded-full ${budgetUtilization > 90 ? 'bg-red-600' : budgetUtilization > 75 ? 'bg-yellow-500' : 'bg-green-600'}`}
                                    style={{ width: `${budgetUtilization}%` }}
                                ></div>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="bg-gray-50 p-2 rounded">
                                <p className="text-gray-500">Total Budget</p>
                                <p className="font-semibold">${totalBudget.toLocaleString()}</p>
                            </div>
                            <div className="bg-gray-50 p-2 rounded">
                                <p className="text-gray-500">Amount Spent</p>
                                <p className="font-semibold">${totalSpent.toLocaleString()}</p>
                            </div>
                            <div className="bg-gray-50 p-2 rounded">
                                <p className="text-gray-500">Remaining</p>
                                <p className="font-semibold">${remainingBudget.toLocaleString()}</p>
                            </div>
                            <div className="bg-gray-50 p-2 rounded">
                                <p className="text-gray-500">Burn Rate</p>
                                <p className="font-semibold">${(totalSpent / 6).toLocaleString()}/mo</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Procurement Overview */}
                <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-gray-800 flex items-center">
                            <ShoppingCart className="mr-2 text-blue-600" size={20} />
                            Procurement Status
                        </h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                            procurementStatus === 'Approved' ? 'bg-green-100 text-green-800' : 
                            procurementStatus === 'Rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                            {procurementStatus}
                        </span>
                    </div>
                    
                    <div className="space-y-3">
                        {procurementItems.map((item, index) => (
                            <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                <div>
                                    <p className="font-medium text-gray-800">{item.item}</p>
                                    {item.date && <p className="text-xs text-gray-500">{item.date}</p>}
                                </div>
                                <span className={`px-2 py-1 rounded text-xs ${
                                    item.status === 'Delivered' ? 'bg-green-100 text-green-800' : 
                                    item.status === 'Ordered' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {item.status}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Budget Details */}
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">Budget Breakdown</h4>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Allocated</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spent</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">% Spent</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {budgetItems.map((item, index) => {
                                const remaining = item.allocated - item.spent;
                                const percentSpent = (item.spent / item.allocated) * 100;
                                return (
                                    <tr key={index}>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{item.category}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">${item.allocated.toLocaleString()}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">${item.spent.toLocaleString()}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">${remaining.toLocaleString()}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="w-16 bg-gray-200 rounded-full h-1.5 mr-2">
                                                    <div 
                                                        className={`h-1.5 rounded-full ${percentSpent > 90 ? 'bg-red-600' : percentSpent > 75 ? 'bg-yellow-500' : 'bg-green-600'}`}
                                                        style={{ width: `${percentSpent}%` }}
                                                    ></div>
                                                </div>
                                                <span className="text-xs">{percentSpent.toFixed(0)}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Review Section */}
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold text-gray-800">Compliance Review</h4>
                    {!isEditing ? (
                        <button 
                            onClick={() => setIsEditing(true)}
                            className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition"
                        >
                            Edit Review
                        </button>
                    ) : (
                        <div className="space-x-2">
                            <button 
                                onClick={() => setIsEditing(false)}
                                className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 transition"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSave}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition"
                            >
                                Save Review
                            </button>
                        </div>
                    )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Budget Status</label>
                        {isEditing ? (
                            <select 
                                value={budgetStatus}
                                onChange={(e) => setBudgetStatus(e.target.value)}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            >
                                <option>Pending</option>
                                <option>Approved</option>
                                <option>Rejected</option>
                                <option>Needs Revision</option>
                            </select>
                        ) : (
                            <div className="p-2 bg-gray-50 rounded">
                                {budgetStatus}
                            </div>
                        )}
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Procurement Status</label>
                        {isEditing ? (
                            <select 
                                value={procurementStatus}
                                onChange={(e) => setProcurementStatus(e.target.value)}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            >
                                <option>Pending</option>
                                <option>Approved</option>
                                <option>Rejected</option>
                                <option>Needs Revision</option>
                            </select>
                        ) : (
                            <div className="p-2 bg-gray-50 rounded">
                                {procurementStatus}
                            </div>
                        )}
                    </div>
                    
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Budget Notes</label>
                        {isEditing ? (
                            <textarea 
                                value={budgetNotes}
                                onChange={(e) => setBudgetNotes(e.target.value)}
                                rows="3"
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            />
                        ) : (
                            <div className="p-2 bg-gray-50 rounded min-h-[60px]">
                                {budgetNotes || <span className="text-gray-400">No notes provided</span>}
                            </div>
                        )}
                    </div>
                    
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Procurement Notes</label>
                        {isEditing ? (
                            <textarea 
                                value={procurementNotes}
                                onChange={(e) => setProcurementNotes(e.target.value)}
                                rows="3"
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            />
                        ) : (
                            <div className="p-2 bg-gray-50 rounded min-h-[60px]">
                                {procurementNotes || <span className="text-gray-400">No notes provided</span>}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BudgetProcurementPanel;